/**
 * EarnestMoneyNeutralPanel — shown to both parties, identically, on an ended
 * Wisconsin deal (design §4.2; copy §8).
 *
 * It quotes the signed offer's own earnest money section, paragraph (e) and
 * the Default section side by side (quoting only one would tilt the panel),
 * and offers the factual termination record. It never says the money was
 * returned or forfeited, never contacts the holder and has no dispute button.
 *
 * Data: `GET /api/transactions/{id}/earnest-money` (see
 * contingencyNoticesAgent.getEarnestMoneyPanel). Without a signed text to
 * quote (`signed_text_available: false`) `WI.earnest_money.signed_text_unavailable`
 * replaces both the holder line and the quotes; a passage the signed text
 * doesn't contain shows `WI.earnest_money.quote_not_found` under its label
 * (never the current template's words). A 404 `not_terminated` (a stale view
 * of a deal that hasn't ended) shows the statements only, without an error.
 *
 * Copy §15.2: when the offer was recorded as ended by a document both parties
 * signed (`terminationReason = admin_record_agreement`), `WI.earnest_money.agreement_line`
 * follows the record line. It never says what that document says about the money.
 */

import React, { useId, useState } from 'react';
import {
  TERMINATION_ERROR_CODES,
  downloadTerminationRecord,
  noticeErrorInfo,
  useEarnestMoneyPanel,
} from '../../../agents/contingencyNoticesAgent';
import { contractCopy, hasPlaceholder } from './contractCopy';
import { REASON_ADMIN_AGREEMENT } from './contractFormat';
import styles from './EarnestMoneyNeutralPanel.module.css';

function Quote({ labelKey, vars, text, testId }) {
  const label = contractCopy(labelKey, vars);
  return (
    <figure className={styles.quote} data-testid={testId} data-found={text ? 'true' : 'false'}>
      <figcaption className={styles.quoteLabel}>{hasPlaceholder(label) ? null : label}</figcaption>
      {text
        ? <blockquote className={styles.quoteText}>{text}</blockquote>
        : <p className={styles.muted}>{contractCopy('WI.earnest_money.quote_not_found')}</p>}
    </figure>
  );
}

export function TerminationRecordButton({ transactionId, className }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const download = async () => {
    setLoading(true);
    setError(null);
    try {
      await downloadTerminationRecord(transactionId);
    } catch (err) {
      const info = noticeErrorInfo(err);
      // 404 not_terminated: a stale view; 409 termination_pending and 503
      // termination_record_unavailable carry their own message.
      setError(info.status === 404 || info.code === TERMINATION_ERROR_CODES.NOT_TERMINATED
        ? "The termination record isn't available yet. Please try again later."
        : info.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.recordWrap}>
      <button
        type="button"
        className={className || styles.recordBtn}
        onClick={download}
        disabled={loading}
        data-testid="termination-record-download"
      >
        {loading ? 'Preparing…' : contractCopy('WI.earnest_money.record_button')}
      </button>
      {error && <p className={styles.error} role="alert">{error}</p>}
    </div>
  );
}

export default function EarnestMoneyNeutralPanel({ transactionId, terminationReason = null }) {
  const titleId = `earnest-money-title-${useId()}`;
  const { data, loading, error, unavailable, reload } = useEarnestMoneyPanel(transactionId);
  const quotes = data?.quotes || {};
  const signedTextUnavailable = data?.signed_text_available === false;
  const hasQuotes = Boolean(data) && !signedTextUnavailable;
  const holder = data?.holder_label
    ? contractCopy('WI.earnest_money.holder_line', { holder_label: data.holder_label })
    : contractCopy('WI.earnest_money.holder_unknown');

  return (
    <section className={styles.panel} aria-labelledby={titleId} data-testid="earnest-money-panel">
      <h3 id={titleId} className={styles.title}>{contractCopy('WI.earnest_money.title')}</h3>
      <p className={styles.intro}>{contractCopy('WI.earnest_money.intro')}</p>

      {loading && <p className={styles.muted} role="status">Loading what your signed offer says…</p>}
      {error && (
        <p className={styles.error} role="alert">
          {error}{' '}
          <button type="button" className={styles.retry} onClick={reload}>Try again</button>
        </p>
      )}

      {!loading && !error && (
        <>
          {signedTextUnavailable ? (
            <p className={styles.holder} data-testid="earnest-money-signed-text-unavailable">
              {contractCopy('WI.earnest_money.signed_text_unavailable')}
            </p>
          ) : (
            <p className={styles.holder} data-testid="earnest-money-holder">{holder}</p>
          )}
          {hasQuotes && (
            <div className={styles.quotes}>
              <h4 className={styles.quotesHeading}>{contractCopy('WI.earnest_money.quotes_heading')}</h4>
              <div className={styles.quoteGrid}>
                <Quote
                  labelKey="WI.earnest_money.quote.earnest_money_label"
                  vars={{ earnest_money_section_ref: data.earnest_money_section_ref }}
                  text={quotes.earnest_money}
                  testId="earnest-money-quote-earnest_money"
                />
                <Quote
                  labelKey="WI.earnest_money.quote.contingency_label"
                  vars={{ how_ref_e: data.how_ref_e }}
                  text={quotes.contingency}
                  testId="earnest-money-quote-contingency"
                />
                <Quote
                  labelKey="WI.earnest_money.quote.default_label"
                  vars={{ default_section_ref: data.default_section_ref }}
                  text={quotes.default}
                  testId="earnest-money-quote-default"
                />
              </div>
            </div>
          )}
          {hasQuotes && quotes.earnest_money && data.release_paragraph_present === false && (
            <p className={styles.muted}>{contractCopy('WI.earnest_money.no_release_paragraph')}</p>
          )}
          {unavailable && <span className={styles.srOnly}>The quoted offer text isn't available yet.</span>}
        </>
      )}

      <p className={styles.recordLine}>{contractCopy('WI.earnest_money.record_line')}</p>
      {terminationReason === REASON_ADMIN_AGREEMENT && (
        <p className={styles.recordLine} data-testid="earnest-money-agreement-line">
          {contractCopy('WI.earnest_money.agreement_line')}
        </p>
      )}
      <TerminationRecordButton transactionId={transactionId} />
    </section>
  );
}
